import { useEffect, useState } from "react";
import classes from "./PaymentMethods.module.scss";
import braintree, { ApplePay, type Client } from "braintree-web";
import { trackError } from "@/utils/error-monitoring";
import { useDonationFormStore } from "@/stores";
import { ORGANIZATION_NAME } from "@/config";
import { getAsset } from "@/utils/assets";
import { getCountryName } from "@/utils/countries";

export type ApplePayButtonProps = {
  amount: number;
  braintreeClient: Client | null;
  onSuccess: () => void;
};

const APPLE_PAY_VERSION = 3;

interface ApplePayValidateMerchantEvent {
  validationURL: string;
}

interface ApplePayPaymentAuthorizedEvent {
  payment: {
    token: unknown;
    shippingContact?: {
      emailAddress?: string;
    };
    billingContact?: {
      addressLines?: string;
      subLocality?: string;
      locality?: string;
      administrativeArea?: string;
      postalCode?: string;
      countryCode?: string;
      country?: string;
      givenName?: string;
      familyName?: string;
      email?: string;
    };
  };
}

function applePaySupported() {
  return (
    window.ApplePaySession &&
    // @ts-expect-error: ApplePaySession supportsVersion is not recognized by TypeScript
    window.ApplePaySession.supportsVersion(APPLE_PAY_VERSION) &&
    // @ts-expect-error: ApplePaySession canMakePayments is not recognized by TypeScript
    window.ApplePaySession.canMakePayments()
  );
}

export function ApplePayButton(props: ApplePayButtonProps) {
  const { amount, braintreeClient, onSuccess } = props;

  const [isLoading, setIsLoading] = useState(true);
  const [applePayInstance, setApplePayInstance] = useState<ApplePay>();
  const [isApplePaySupported, setIsApplePaySupported] = useState(false);
  const { setNonce, setAddress, setIsManualAddress, setPersonalDetails } =
    useDonationFormStore();

  useEffect(() => {
    if (!braintreeClient) return;
    const isSupported = applePaySupported();

    setIsApplePaySupported(isSupported);

    const initializeApplePay = async () => {
      try {
        const appleInstance = await braintree.applePay.create({
          client: braintreeClient,
        });
        setApplePayInstance(appleInstance);
      } catch (error) {
        trackError(error as Error);
      } finally {
        setIsLoading(false);
      }
    };

    if (isSupported) {
      initializeApplePay();
    }
  }, [braintreeClient]);

  const handleApplePayment = async () => {
    if (!applePayInstance) return;

    try {
      const paymentRequest = applePayInstance.createPaymentRequest({
        total: {
          label: "Donation",
          amount: `${amount}`,
        },
        requiredShippingContactFields: ["email"],
        requiredBillingContactFields: ["postalAddress"],
      });

      // @ts-expect-error: ApplePaySession is not recognized by TypeScript
      const session = new window.ApplePaySession(
        APPLE_PAY_VERSION,
        paymentRequest,
      );

      session.onvalidatemerchant = (event: ApplePayValidateMerchantEvent) => {
        applePayInstance
          .performValidation({
            validationURL: event.validationURL,
            displayName: ORGANIZATION_NAME,
          })
          .then((merchantSession) => {
            session.completeMerchantValidation(merchantSession);
          })
          .catch(() => {
            session.abort();
          });
      };

      session.onpaymentauthorized = (event: ApplePayPaymentAuthorizedEvent) => {
        const payment = event.payment;

        applePayInstance
          .tokenize({ token: payment.token })
          .then((payload) => {
            if (!payload.nonce) {
              throw new Error("No nonce found");
            }

            setNonce(payload.nonce);
            const email = payment.shippingContact?.emailAddress || "";

            const firstName = payment.billingContact?.givenName || "";
            const lastName = payment.billingContact?.familyName || "";

            setPersonalDetails({
              title: "",
              firstName,
              lastName,
              email,
              confirmEmail: email,
            });

            const lines = payment.billingContact?.addressLines || [];
            const addressLine1 = lines[0] || "";
            const addressLine2 = lines[1] || "";

            const city = payment.billingContact?.locality || "";
            const region = payment.billingContact?.administrativeArea || "";
            const postalCode = payment.billingContact?.postalCode || "";
            const country = getCountryName(payment.billingContact?.countryCode);

            setAddress({
              "address-line-1": addressLine1,
              "address-line-2": addressLine2,
              city,
              region,
              "postal-code": postalCode,
              country,
            });

            setIsManualAddress(true);
            onSuccess();
          })
          .then(() => {
            // @ts-expect-error ApplePaySession STATUS constants
            session.completePayment(window.ApplePaySession.STATUS_SUCCESS);
          })
          .catch(() => {
            // @ts-expect-error ApplePaySession STATUS constants
            session.completePayment(window.ApplePaySession.STATUS_FAILURE);
          });
      };

      session.begin();
    } catch (error) {
      if (error instanceof Error && error.name === "AbortError") return;

      trackError(error as Error);
    }
  };

  if (!isApplePaySupported) {
    return null;
  }

  return (
    <button
      type="button"
      className={classes.button}
      onClick={handleApplePayment}
      disabled={isLoading}
    >
      <span>Donate with</span>
      <img
        src={getAsset("/images/apple-pay.svg")}
        className={classes.icon}
        alt="Apple Pay"
        width={85}
        height={35}
      />
    </button>
  );
}
